/**
 * Typed HTTP client for the panel API.
 *
 * - Reads/writes the auth token to localStorage.
 * - Auto-refreshes on 401 once (using the stored refresh token), then retries.
 * - Throws a `ApiError` with status + body details on non-2xx so React Query
 *   can render meaningful messages.
 */

const STORAGE_KEY = "mfa.session";

export type Session = {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: {
    id: string;
    username: string;
    email: string;
    fullName: string;
    role: string;
    perms: string[];
  };
};

/**
 * Backend login response → Session adapter. The login endpoint returns
 * `{ accessToken, refreshToken, refreshExpiresAt, user: { ... } }` plus a few
 * extras we don't need on the UI.
 */
export type LoginResponse = {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresAt: string;
  user: Session["user"];
};

export function sessionFromLogin(r: LoginResponse): Session {
  return {
    accessToken: r.accessToken,
    refreshToken: r.refreshToken,
    expiresAt: r.refreshExpiresAt,
    user: r.user,
  };
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function saveSession(s: Session | null): void {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

/** Refresh promise dedupe — many requests may 401 at the same time. */
let refreshPromise: Promise<string | null> | null = null;
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const cur = loadSession();
  if (!cur?.refreshToken) return null;
  const refreshTokenAtStart = cur.refreshToken;
  refreshPromise = (async () => {
    try {
      const res = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenAtStart }),
      });
      if (!res.ok) return null;
      // The backend may rotate the refresh token on every refresh (recommended
      // pattern: detect reuse → revoke the family). Persist whatever the
      // server returned, falling back to the previous value if it didn't.
      const data = (await res.json()) as {
        accessToken: string;
        refreshToken?: string;
      };

      // Race guard: between starting the refresh and getting here, the user
      // may have logged out (which clears the session) or logged in as a
      // different user (which changes the refresh token). In either case
      // writing `updated` back would resurrect a dead session or stomp the
      // new one with stale data.
      const now = loadSession();
      if (!now || now.refreshToken !== refreshTokenAtStart) {
        return null;
      }
      const updated: Session = {
        ...now,
        accessToken: data.accessToken,
        ...(data.refreshToken ? { refreshToken: data.refreshToken } : {}),
      };
      saveSession(updated);
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export type ApiOptions = RequestInit & {
  /** Skip the implicit Authorization header (used by login/refresh). */
  unauthenticated?: boolean;
  /** When `false`, return raw Response (used by SSE consumers). */
  parse?: boolean;
};

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { unauthenticated, parse = true, headers, ...rest } = options;
  const session = loadSession();
  const init: RequestInit = {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(unauthenticated || !session ? {} : { Authorization: `Bearer ${session.accessToken}` }),
      ...(headers as Record<string, string> | undefined),
    },
  };

  let res = await fetch(`/api/v1${path}`, init);

  // Auto-refresh on 401 (once)
  if (res.status === 401 && !unauthenticated && session?.refreshToken) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      init.headers = { ...(init.headers as Record<string, string>), Authorization: `Bearer ${fresh}` };
      res = await fetch(`/api/v1${path}`, init);
    } else {
      saveSession(null);
      // Force a re-login by reloading; auth gate will redirect.
      window.location.assign("/login");
      throw new ApiError(401, "session expired", null);
    }
  }

  if (!parse) return res as unknown as T;

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    const msg =
      (body as { error?: string; message?: string })?.error ??
      (body as { message?: string })?.message ??
      `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}
